import React, { useState, useEffect, useCallback } from "react";
import {
  usePagination,
  useTable,
  useFilters,
  useGlobalFilter,
  TableOptions,
} from "react-table";
import { Button, Col, Form, Pagination, Row, Table } from "react-bootstrap";
import ColumnFilter from "./ColumnFilter";
import { Spinner } from "../../icons/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons";
import { EpaGridOrderBy, EpaGridOrderByFetchParams } from "../EpaGridProps";
import { fetchWithCancel } from "./CancellableFetch";

//https://react-table-v7.tanstack.com/docs/examples/data-driven-classes-and-styles

// Server side pagination: https://react-table-v7.tanstack.com/docs/faq#how-can-i-debounce-rapid-table-state-changes
export interface FetchGridProps extends TableOptions<Record<string, any>> {
  enableQuickFind?: boolean;
  enablePagination?: boolean;
  exportToCsvFunction?: (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any
  ) => string;
  fetchUrlFunction: (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any,
    orderBy?: EpaGridOrderByFetchParams
  ) => string;
  //format an array response into other formatting
  formatFunction?: (rows: Array<any>) => Array<any>;
  //format any response format to convert into an already formatted response with an OpenApi equivalent format
  formatResponseFunction?: (response: any) => any;
  searchText?: string;
  orderBy?: EpaGridOrderBy;
  defaultRecordsPerPage?: number;
}

const FetchGrid = (props: FetchGridProps) => {
  const PAGE_NO = 0;

  const [quickFindSearch, setQuickFindSearch] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(
    props.defaultRecordsPerPage ? props.defaultRecordsPerPage : 10
  );
  const [totalCount, setTotalCount] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [totalPage, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const { columns, data } = props;
  const exportToCsvFn = props.exportToCsvFunction;
  const fetchUrlFn = props.fetchUrlFunction;
  const formatFn = props.formatFunction;
  const formatAnyResponseFn = props.formatResponseFunction;
  const enablePagination = props.enablePagination === true;
  const enableQuickFind = props.enableQuickFind === true;
  const propsSearchText = props.searchText;
  const [orderByColumnName, setOrderByColumnName] = useState("");
  const [orderByAscending, setOrderByAscending] = useState(false);
  const orderBy = props.orderBy;

  let lastToken = { cancel: function () {} };

  const fetchRowData = useCallback(
    async (
      pageNo: number,
      recordsPerPage: number,
      searchText: string,
      filters: any,
      fetchUrlFn: any,
      orderByName: string,
      orderByAsc: boolean
    ) => {
      try {
        const fetchUrl = fetchUrlFn(
          pageNo,
          recordsPerPage,
          propsSearchText ? propsSearchText : searchText,
          filters,
          orderBy
            ? {
                orderByName: orderBy.orderByName,
                orderByAscending: orderBy.orderByAscending,
              }
            : null
        );

        /*
        const fetchPromise = fetch(fetchUrl);
        const res = await fetchPromise;
        const json = await res.json();
        */

        lastToken.cancel();
        const fetchPromise = fetchWithCancel(fetchUrl, lastToken);
        const json = await fetchPromise;

        const reponseData = formatAnyResponseFn
          ? formatAnyResponseFn(json)
          : json;

        const responseTableData = formatFn
          ? formatFn(reponseData)
          : reponseData?.list || [];

        setTableData(responseTableData);
        setTotalCount(reponseData?.count || 0);
        setTotalPages(
          (recordsPerPage > 0 &&
            Math.floor(reponseData?.count / recordsPerPage) + 1) ||
            1
        );
        setLoading(false);
      } catch (err) {
        console.log("Fetch request cancelled");
      }
    },
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: ColumnFilter,
    }),
    []
  );

  const toggleSortBy = (columnName: string) => {
    if (columnName === orderByColumnName) {
      setOrderByAscending(!orderByAscending);
    } else {
      setOrderByColumnName(columnName);
      setOrderByAscending(true);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    setAllFilters,
    // Get the state from the instance
    state: { pageIndex, pageSize, globalFilter, filters },
  } = useTable(
    {
      columns,
      data: tableData,
      defaultColumn,
      manualPagination: true,
      manualGlobalFilter: true,
      manualFilters: true,
      manualSortBy: true,
      initialState: {
        pageIndex: PAGE_NO,
        pageSize: recordsPerPage,
      },
      pageCount: totalPage,
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  useEffect(() => {
    setLoading(true);
    fetchRowData(
      pageIndex + 1,
      recordsPerPage,
      propsSearchText ? propsSearchText : quickFindSearch,
      filters,
      fetchUrlFn,
      orderByColumnName,
      orderByAscending
    );
  }, [
    pageIndex,
    recordsPerPage,
    quickFindSearch,
    filters,
    fetchUrlFn,
    propsSearchText,
    orderByColumnName,
    orderByAscending,
  ]);

  useEffect(() => {
    setRecordsPerPage(pageSize);
    gotoPage(0);
  }, [pageSize, gotoPage]);

  return (
    <div>
      <Row>
        {enableQuickFind && (
          <Col sm="12" md="6">
            <Form.Control
              type="text"
              name="quickFind"
              placeholder="Search..."
              value={quickFindSearch || ""}
              onChange={(e) => {
                setQuickFindSearch(e.target.value);
              }}
            />
          </Col>
        )}
        <Col sm="12" md="6">
          <Button
            className={enableQuickFind ? "float-end" : ""}
            disabled={filters.length == 0}
            onClick={() => setAllFilters([])}
          >
            Clear All Filters
          </Button>
        </Col>
      </Row>
      <Table className="mt-2" striped {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>
                  {column.render("Header")}
                  {/* Add a sort direction indicator */}
                  {orderBy &&
                    orderBy.enabledOrderByColumns &&
                    orderBy.enabledOrderByColumns?.filter(
                      (colName) => colName === column.Header
                    ).length > 0 && (
                      <span
                        onClick={(e) => toggleSortBy(column.Header as string)}
                      >
                        {column.Header === orderByColumnName ? ( //isSorted
                          orderByAscending ? (
                            <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                          ) : (
                            <FontAwesomeIcon
                              icon={faSortDown}
                              className="ms-2"
                            />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        )}
                      </span>
                    )}
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                <Spinner />
              </td>
            </tr>
          )}
          {!loading &&
            rows.length > 0 &&
            rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          {!loading && rows.length == 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {enablePagination && (
        <Row>
          <Col md="6">
            <span className="mx-2">
              {"Page "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </span>
            <span className="ms-3 me-2">Show:</span>
            <Form.Select
              className="d-inline-block w-auto"
              value={pageSize}
              onChange={(e: any) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 50, 100, 250, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>

            <span className="ms-3 me-2">Go to page:</span>
            <Form.Control
              className="d-inline-block"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: "75px" }}
            />
            <span className="ms-3 me-2">
              {"Total number of records: "}
              {totalCount}
            </span>
          </Col>
          <Col md="6">
            <Pagination className="float-end">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
          </Col>
        </Row>
      )}
      {exportToCsvFn && (
        <Row>
          <Col sm="12" md="6">
            <Button
              className="primary"
              name="exportToCsv"
              onClick={(e) => {
                window.open(
                  exportToCsvFn(
                    pageIndex + 1,
                    recordsPerPage,
                    propsSearchText ? propsSearchText : quickFindSearch,
                    filters
                  ),
                  "_blank"
                );
              }}
            >
              Export to CSV
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};

export { FetchGrid };
